<script lang="ts">
  import { getContext } from 'svelte';
  import { type ColorMode } from '@squareup/public-web-design-system';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import LeadForm from './LeadForm.svelte';
  import LeadFormIntroPartial from './LeadFormIntroPartial.svelte';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';
  import type { LeadFormProps, LeadFormSectionProps } from './types';

  export let entryId: LeadFormProps['entryId'];
  export let customCssClass: LeadFormProps['customCssClass'];
  export let title: LeadFormProps['title'];
  export let body: LeadFormProps['body'];
  export let introHeadingLevel: LeadFormProps['introHeadingLevel'];
  export let introBodyFontSize: LeadFormProps['introBodyFontSize'];
  export let introTextAlignment: LeadFormProps['introTextAlignment'];

  // Passed down to LeadForm
  export let buttonLabel: LeadFormProps['buttonLabel'];
  export let background: LeadFormProps['background'];
  export let followUpList: LeadFormProps['followUpList'];
  export let visibleRows: LeadFormProps['visibleRows'];
  export let hiddenFields: LeadFormProps['hiddenFields'];
  export let legalField: LeadFormProps['legalField'];
  export let richTextAlignment: LeadFormProps['richTextAlignment'];
  export let formId: LeadFormProps['formId'];
  export let submitUrl: LeadFormProps['submitUrl'];
  export let successfulSubmitBannerMessage: LeadFormProps['successfulSubmitBannerMessage'];
  export let successfulSubmitBannerDuration: LeadFormProps['successfulSubmitBannerDuration'];
  export let disableBanner: LeadFormProps['disableBanner'];
  export let areMarketStylesEnabled: LeadFormProps['areMarketStylesEnabled'];
  export let locale: LeadFormProps['locale'];

  // Passed down to phone field
  export let localizedCountryNames: LeadFormProps['localizedCountryNames'];

  // Hardcoded translations passed down
  export let successfulSubmitButtonText: LeadFormProps['successfulSubmitButtonText'];
  export let failedSubmitBannerMessage: LeadFormProps['failedSubmitBannerMessage'];

  export let followUpLeadForm: LeadFormSectionProps['followUpLeadForm'];

  const isPWDSEnabled = getContext<DesignSystemEnum>('designSystem') === DesignSystemEnum.PublicWebDesignSystem;

  let leadFormSection: HTMLElement;
  let initialLeadFormContainer: HTMLElement;
  let followUpFormContainer: HTMLElement;

  const showFollowUpForm = (email: string) => {
    // Passing on email from previous form submission
    (followUpFormContainer.querySelector('#Email') as HTMLInputElement).value = email;

    // Set height statically to its computed value so we have a starting value to animate from
    leadFormSection.style.height = `${leadFormSection.offsetHeight}px`;

    // Temporarily display followUpForm (invisibly) so we can calculate its height
    followUpFormContainer.style.display = 'block';
    const followUpHeight = followUpFormContainer.offsetHeight;
    followUpFormContainer.style.display = 'none';

    // Animate to the new height we will need to fit followUpForm and animate out initial form
    leadFormSection.style.height = `${followUpHeight}px`;
    initialLeadFormContainer.style.opacity = '0';
    initialLeadFormContainer.style.transform = 'translate3d(0, -50%, 0)';

    // Animate in follow up form once initial is gone and height is fitting
    initialLeadFormContainer.addEventListener('transitionend', () => {
      initialLeadFormContainer.style.display = 'none';
      followUpFormContainer.style.display = 'block';
      followUpFormContainer.style.visibility = 'visible';

      // Removing static height to let any resizing work properly now that animation is done
      leadFormSection.style.removeProperty('height');

      // Make sure all of the above has finished before displaying follow-up
      setTimeout(() => {
        followUpFormContainer.style.opacity = '1';
        followUpFormContainer.style.transform = 'translate3d(0, 0, 0)';
      }, 0);
    });
  };

  const colorMode: ColorMode = background === 'black' ? 'dark' : 'light';
</script>

<div
  class="LeadFormSection {customCssClass}"
  class:LeadFormSection--is-pwds={isPWDSEnabled}
  class:darkTheme={background === 'black'}
  bind:this={leadFormSection}
>
  {#if visibleRows && visibleRows.length > 0}
    <div class="InitialLeadForm" bind:this={initialLeadFormContainer}>
      <div class="content InitialLeadForm__content" class:content-width-medium={!isPWDSEnabled} data-entry-id={entryId}>
        {#if title || body}
          <LeadFormIntroPartial {title} {body} {introHeadingLevel} {introBodyFontSize} {introTextAlignment} />
        {/if}

        <LeadForm
          {buttonLabel}
          {followUpList}
          {visibleRows}
          {hiddenFields}
          {legalField}
          {richTextAlignment}
          {formId}
          {submitUrl}
          {localizedCountryNames}
          {successfulSubmitBannerMessage}
          {successfulSubmitBannerDuration}
          {successfulSubmitButtonText}
          {failedSubmitBannerMessage}
          {disableBanner}
          hasFollowUpForm={Boolean(followUpLeadForm)}
          {showFollowUpForm}
          {areMarketStylesEnabled}
          {locale}
          {colorMode}
        />
      </div>
    </div>

    {#if followUpLeadForm}
      <div class="FollowUpLeadForm" bind:this={followUpFormContainer}>
        <DynamicChildrenComponent children={followUpLeadForm} passThroughProps={{ colorMode }} />
      </div>
    {/if}
  {/if}
</div>

<style lang="scss">
  $ease-out-quad: cubic-bezier(0.25, 0.46, 0.45, 0.94);
  $ease-in-out-quad: cubic-bezier(0.455, 0.03, 0.515, 0.955);

  @layer standard-components {
    .LeadFormSection {
      transition: 250ms height $ease-out-quad;

      &.darkTheme {
        --color: var(--color-white);
      }
    }

    .InitialLeadForm {
      display: block;
      transform: translate3d(0, 0, 0);
      opacity: 1;
      transition:
        250ms opacity $ease-out-quad,
        250ms transform $ease-out-quad;
    }

    .FollowUpLeadForm {
      display: none;
      opacity: 0;
      visibility: hidden;
      transform: translate3d(0, 25%, 0);
      transition:
        250ms opacity $ease-in-out-quad,
        250ms transform $ease-in-out-quad;
    }
  }
</style>
